import { useDispatch, useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import {
  selectDialogConfig,
  selectDialogContents,
  selectDialogIsLoading,
  selectDialogIsOpen,
  updateSubmission,
} from '../../redux/features/dialogs/dialog.slice';
import { getValidationsThunk } from '../../redux/features/submission/submission.thunk.validations';
import { uiSchemaProperties } from '../../shared/enums/uiSchemaProperties';
import useProcedures from '../../procedure/useProcedures';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { callApi } from '../../api/helpers/api.helpers';
import draftApi from '../../api/draft.api';

export function useGenericDialog() {
  const dispatch = useDispatch();

  const { dialogProcedure, validationSchemaProps, uiSchemaProps } = useSelector(selectDialogConfig);
  const isOpen = useSelector(selectDialogIsOpen);
  const { submission, isValid, uiSchema: dialogUiSchema } = useSelector(selectDialogContents);
  const isLoading = useSelector(selectDialogIsLoading);
  const { getProcedure } = useProcedures();

  const { resource: validationSchema } = useConfiguration(validationSchemaProps, dialogProcedure?.id);
  const { resource: uiSchema } = useConfiguration(uiSchemaProps, dialogProcedure?.id);

  const dialogId = uiSchema?.[uiSchemaProperties.DIALOG_ID];
  const dialogBusinessRulesPrefix = uiSchema?.[uiSchemaProperties.DIALOG_BUSINESS_RULES_PREFIX];
  const dialogTitle = uiSchema?.[uiSchemaProperties.DIALOG_TITLE];
  const dialogDescription = uiSchema?.[uiSchemaProperties.DIALOG_DESCRIPTION];
  const dialogActions = uiSchema?.[uiSchemaProperties.DIALOG_ACTIONS];
  const dialogOptions = uiSchema?.[uiSchemaProperties.DIALOG_OPTIONS];

  const onValidate = useDebouncedCallback(async ({ formData, errors }, controller) => {
    const procedure = getProcedure(formData?.procedure);
    dispatch(
      getValidationsThunk({
        submission: {
          ...formData,
          receivingOffice: procedure?.receivingOffice,
        },
        overrideProcedure: dialogProcedure.id,
        validationErrors: errors,
        uiSchema,
        dialogId,
        dialogBusinessRulesPrefix,
        controller,
      }),
    );
  }, 500);

  const onSectionChange = async ({ formData, changeProps }) => {
    if (changeProps.sectionName) {
      // section level change
      if (!changeProps.sectionItemId) {
        // non submitted entity canceled (no post-processing here)
        return formData;
      }
      const { sectionName, sectionOperation, sectionItemId } = changeProps;
      const event = `${sectionName}_${sectionOperation}`;
      return calculateAndPostProcess(formData, event, sectionItemId);
    }
    // field level change
    const { propertyId } = changeProps;
    const event = `${propertyId}_change`;
    return calculateAndPostProcess(formData, event, propertyId);
  };

  async function calculateAndPostProcess(formData, event = 'undefined', id) {
    const newFormData = await callApi(draftApi.postProcessBusinessRules)(formData, event, id);
    onValidate({ formData: newFormData });
    updateSubmission(newFormData);
  }

  return {
    dialogConfig: {
      id: dialogId,
      businessRulesPrefix: dialogBusinessRulesPrefix,
      title: dialogTitle,
      description: dialogDescription,
      procedure: dialogProcedure,
      actions: dialogActions,
      options: dialogOptions,
    },
    isOpen,
    isLoading,
    validationSchema,
    uiSchema: dialogUiSchema ?? uiSchema,
    isValid,
    submission,
    onValidate,
    onSectionChange,
  };
}
